module.exports = {
  presentationApp: {
    labels: {
      video: "Video",
      file: "File",
      fileTemplate: "File template",
      openMedia: "Open in fullscreen preview",
      closeMedia: "Close preview",
      fullscreenPreview: "Fullscreen preview"
    },
    cover: {
      kicker: "SMART-TEAM-DEV-PORTAL",
      title: "Corporate portal",
      lead: "A web platform for centralised access to corporate applications, data, and services.",
      note:
        "Consolidates document management, financial modules, banking APIs, access control, laboratory records, transport monitoring, and AI assistant. Deployed on own infrastructure.",
      alt: "Corporate portal home screen",
      video: "overview of the home screen and working modules of the portal"
    },
    about: {
      kicker: "Platform",
      title: "Node.js platform with modular architecture",
      lead:
        "Server: Node.js, frontend: React/Webpack. Each section is an independent module with its own business logic, routing and API. More than 30 applications in a single shell.",
      items: [
        "modular structure: each application is independent",
        "role-based access control via AD groups",
        "REST API for integrations with external systems",
        "TypeScript typing, unified codebase"
      ],
      primaryAlt: "Corporate portal architecture",
      secondaryAlt: "User workspace",
      mediaCaption: "",
      video: ""
    },
    roles: {
      kicker: "Access",
      title: "Role model based on AD groups",
      lead:
        "The set of available applications and data is determined by the user's role linked to Active Directory. Role management is centralised and requires no code changes.",
      keyTitle: "",
      items: [
        "managers",
        "office staff",
        "engineers and technical specialists",
        "accounting",
        "HR teams",
        "IT administrators",
        "external users"
      ],
      mediaAlt: "Role model and user access",
      mediaCaption: "",
      video: "example of role-based access separation"
    },
    tasks: {
      kicker: "Functionality",
      title: "Tasks covered by the platform",
      lead:
        "The platform covers key workflows: document management, financial records, banking operations, laboratory data, monitoring, and automation.",
      items: [
        "access management (AD, RBAC)",
        "document workflow",
        "banking operations and statements",
        "laboratory modules and standards",
        "access control and working time",
        "transport and telemetry (FortMonitor)",
        "AI assistant based on OpenAI API",
        "audit and system logging"
      ],
      mediaAlt: "Corporate portal ecosystem",
      mediaCaption: "Photo: portal ecosystem and key work directions",
      video: "short walkthrough of the main processes"
    },
    workflow: {
      kicker: "Workflow",
      title: "Typical user path",
      lead:
        "All modules follow a unified navigation model. The user authenticates once and gets access to all permitted applications without re-login.",
      steps: [
        { title: "authentication", text: "Login via AD or the built-in authentication system." },
        { title: "workspace", text: "List of available modules, notifications, widgets." },
        { title: "module", text: "Navigate to the required application via icon or direct link." },
        { title: "operation", text: "Work with data: document, form, record, table." },
        { title: "result", text: "Status, notification, report, file, or record in the system." }
      ],
      mediaAlt: "Application selection scenario in the portal",
      mediaCaption: "Photo: transition from the workspace to an application",
      video: "user path from login to result",
      lineTitle: "Typical scenario:",
      lineText: "authentication → workspace → module → operation → result"
    },
    desktop: {
      kicker: "Workspace",
      title: "Configurable start screen",
      lead:
        "The layout of icons, widgets, and sections is formed based on the user's role. Personalisation is configured via profile settings without server-side changes.",
      items: [
        "personal set of icons and quick links",
        "widgets with current data",
        "adapted by role and department",
        "minimum steps to reach the required function"
      ],
      alt: "Unified digital portal workspace",
      video: "workspace setup for the user's role"
    },
    modules: [
      {
        id: "paperwork",
        number: "07",
        kicker: "Document workflow",
        title: "Documents, records, and attachments",
        lead:
          "Single interface for creating, storing, and collaboratively editing internal documents. Linked to counterparty, employee, and contract records. Collaborative editing via Office Online.",
        bullets: [
          "creating and editing documents",
          "storing attachments in file storage",
          "collaborative editing (Office Online)",
          "links: document — counterparty — contract"
        ],
        image: "/img/presentation_app/logo_docs.png",
        alt: "Documents and document workflow section",
        caption: "Photo: document workflow section and document records",
        file: "/img/presentation_app/logo_docs.png",
        video: "working with a record, attachments, and related data"
      },
      {
        id: "contragents",
        number: "08",
        kicker: "Counterparties",
        title: "Partner records with external verification",
        lead:
          "Integration with Kontur.Focus and DaData: autofill of company details by TIN, reliability check, monitoring of legal status changes.",
        bullets: [
          "autofill of details by TIN (DaData API)",
          "verification via Kontur.Focus database",
          "notifications on status change",
          "links with documents and banking operations"
        ],
        image: "/img/presentation_app/logo_agents.png",
        alt: "Counterparty record and contact data",
        caption: "Photo: counterparty record, details, and statuses",
        file: "/img/presentation_app/logo_agents.png",
        video: "checking details and updating a partner record"
      },
      {
        id: "finance",
        number: "09",
        kicker: "Finance and banking",
        title: "Bank statements and 1C integration",
        lead:
          "Import statements via API of Sberbank, Tinkoff, VTB, Tochka, Alfa-Bank. Match operations with counterparties and contracts, export to 1C.",
        bullets: [
          "connection to multiple bank APIs",
          "automatic import and parsing of statements",
          "matching payments with counterparties",
          "data export in 1C format"
        ],
        image: "/img/presentation_app/logo_finance.png",
        alt: "Portal finance section",
        caption: "Photo: payment journal and processing statuses",
        file: "/img/presentation_app/logo_finance.png",
        video: "importing operations, reconciliation, and navigating to related data"
      },
      {
        id: "laboratory",
        number: "10",
        kicker: "Laboratory modules",
        title: "Equipment, methods, and report tracking",
        lead:
          "Specialised block for laboratory activities: standards directories, equipment and group records, report templates, employee certificates.",
        bullets: [
          "standards and methods directories",
          "equipment and technical group records",
          "report templates (Word/Excel)",
          "employee certificates and clearances"
        ],
        image: "/img/presentation_app/logo_lab.png",
        alt: "Portal laboratory module",
        caption: "Photo: equipment, methods, and report templates",
        file: "/img/presentation_app/logo_lab.png",
        video: "path from a test object to a completed report"
      },
      {
        id: "skud",
        number: "11",
        kicker: "Access control",
        title: "Integration with access control system",
        lead:
          "Automatic collection of access events: passes, shifts, lateness. Server-side processing, attendance and violation reports.",
        bullets: [
          "import of pass events from ACS",
          "shift and lateness tracking",
          "attendance and violation reports",
          "links with HR data"
        ],
        image: "/img/presentation_app/logo_skud.png",
        alt: "Access control and working time module",
        caption: "Photo: access event log and attendance analytics",
        file: "/img/presentation_app/logo_skud.png",
        video: "reviewing an access event, shift, and attendance report"
      },
      {
        id: "transport",
        number: "12",
        kicker: "Transport",
        title: "Waybills and FortMonitor telemetry",
        lead:
          "Digital transport operations: waybills, routes, mileage. Cross-referenced with telemetric data from the FortMonitor API.",
        bullets: [
          "vehicle and waybill records",
          "routes and mileage control",
          "telemetry from FortMonitor API",
          "reconciliation of documents with actual movement"
        ],
        image: "/img/presentation_app/logo_car.png",
        alt: "Transport monitoring and movement",
        caption: "Photo: transport, routes, and industrial monitoring",
        file: "/img/presentation_app/logo_car.png",
        video: "matching a waybill with mileage and route data"
      },
      {
        id: "ai",
        number: "13",
        kicker: "AI assistant",
        title: "Built-in chat based on OpenAI API",
        lead:
          "Integration with OpenAI (GPT-4): analyse uploaded files, system logs, answer questions about corporate data in natural language.",
        bullets: [
          "connection to OpenAI Chat Completions API",
          "analysis of uploaded files and documents",
          "system log and event parsing",
          "contextual search over corporate data"
        ],
        image: "/img/presentation_app/logo_ai.png",
        alt: "AI assistant interface",
        caption: "Photo: example of a request and AI assistant response",
        file: "/img/presentation_app/logo_ai.png",
        video: "analyzing a file, log, or user question"
      },
      {
        id: "security",
        number: "15",
        kicker: "Security",
        title: "Authentication, roles, and audit",
        lead:
          "Authentication via Active Directory (LDAP), role-based access control at application and data level (RBAC), full user activity log.",
        bullets: [
          "Active Directory integration (LDAP)",
          "role-based access management (RBAC)",
          "audit log of all significant operations",
          "data visibility restricted by role"
        ],
        image: "/img/presentation_app/dark_access.png",
        alt: "Roles, AD groups, and personal portal workspace",
        caption: "Photo: roles, AD groups, and access rules",
        file: "/img/presentation_app/dark_access.png",
        video: "assigning a role, checking permissions, and viewing the audit log"
      },
      {
        id: "automation",
        number: "16",
        kicker: "Automation",
        title: "Background tasks and cross-system integrations",
        lead:
          "Task scheduler for recurring operations: data synchronisation, checks, mailings. Integrations implemented via REST API and SOAP.",
        bullets: [
          "background task scheduler (cron jobs)",
          "REST API and SOAP for external systems",
          "notifications via email, Telegram, WhatsApp",
          "automatic synchronisation with AD, 1C, banks"
        ],
        integrations: ["AD", "1C", "banks", "Kontur.Focus", "DaData", "email", "messengers", "ACS", "FortMonitor", "OpenAI"],
        image: "/img/presentation_app/dark_Integration.png",
        alt: "Integrations of the portal's corporate services",
        caption: "Photo: external services and corporate portal integrations",
        file: "/img/presentation_app/dark_Integration.png",
        video: "synchronization, checks, mailings, and data updates"
      }
    ],
    value: {
      kicker: "Summary",
      title: "What the platform delivers",
      lead:
        "A centralised platform reduces the number of fragmented tools, standardises processes, and lowers operational costs.",
      groups: [
        {
          title: "For users",
          bullets: [
            "single interface instead of multiple separate systems",
            "fewer manual and repetitive operations",
            "fast access to the data you need",
            "real-time notifications and statuses"
          ]
        },
        {
          title: "For IT and business",
          bullets: [
            "centralised access management",
            "process transparency through auditing",
            "single codebase for all modules",
            "ready foundation for new applications"
          ]
        }
      ],
      assetTitle: "Benefit icons",
      assetText: "without a mandatory screenshot",
      image: "/img/presentation_app/dark_value.png",
      imageAlt: "Practical value for the user and the company",
      imageCaption: "Photo: practical value of the portal for the user and the company",
      video: "short summary of value for the user and the company"
    },
    conclusion: {
      kicker: "Platform",
      title: "Scalable corporate system",
      lead:
        "More than 30 modules, 10+ integrations with external services, deployed on own infrastructure. The platform is ready for expansion without architectural changes.",
      bullets: [
        "unified interface for all departments",
        "modular architecture without tight coupling",
        "integrations: AD, 1C, banks, ACS, OpenAI",
        "audit, logging, role-based access control",
        "documented API for new integrations"
      ],
      alt: "Corporate portal home screen",
      file: "/img/presentation_app/dark_portal.png",
      video: "final walkthrough of the portal's key capabilities"
    }
  }
};
